import type { ClassValue } from "clsx";
import * as React from "react";

import { cn } from "~/lib/ui/utils/utils";

const Table = ({
  className,
  wrapperClassName,
  ref,
  ...props
}: React.ComponentProps<"table"> & {
  wrapperClassName?: ClassValue;
}) => (
  <div
    className={cn(
      "relative w-full overflow-hidden rounded-xl border-subtle",
      wrapperClassName,
    )}
  >
    <div className="overflow-auto">
      <table
        className={cn("w-full caption-bottom text-sm", className)}
        ref={ref}
        {...props}
      />
    </div>
  </div>
);
Table.displayName = "Table";

const TableHeader = ({
  className,
  ref,
  ...props
}: React.ComponentProps<"thead">) => (
  <thead
    className={cn(
      `
        bg-muted/30

        [&_tr]:border-b [&_tr]:border-border/50
      `,
      className,
    )}
    ref={ref}
    {...props}
  />
);
TableHeader.displayName = "TableHeader";

const TableBody = ({
  className,
  alternatingRows,
  ref,
  ...props
}: React.ComponentProps<"tbody"> & {
  alternatingRows?: boolean;
}) => (
  <tbody
    className={cn(
      alternatingRows &&
        "[&_tr:nth-child(even)]:bg-muted/50! [&_tr:nth-child(odd)]:bg-transparent!",
      className,
    )}
    ref={ref}
    {...props}
  />
);
TableBody.displayName = "TableBody";

const TableRow = ({ className, ref, ...props }: React.ComponentProps<"tr">) => (
  <tr
    className={cn(
      `
        border-b border-border/50 bg-transparent text-muted-foreground transition-colors
        last:border-b-0

        data-[state=selected]:bg-muted

        hover:bg-muted/50
      `,
      className,
    )}
    ref={ref}
    {...props}
  />
);
TableRow.displayName = "TableRow";

const TableHead = ({
  className,
  ref,
  ...props
}: React.ComponentProps<"th">) => (
  <th
    className={cn(
      `
        h-12 px-4 text-left align-middle font-medium text-foreground

        [&:has([role=checkbox])]:pr-0
      `,
      className,
    )}
    ref={ref}
    {...props}
  />
);
TableHead.displayName = "TableHead";

const TableCell = ({
  className,
  ref,
  ...props
}: React.ComponentProps<"td">) => (
  <td
    className={cn(
      `
        p-4 align-middle

        [&:has([role=checkbox])]:pr-0
      `,
      className,
    )}
    ref={ref}
    {...props}
  />
);
TableCell.displayName = "TableCell";

export { Table, TableHeader, TableBody, TableHead, TableRow, TableCell };
